import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ElementRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { PersistedFileInfo } from '../knowledge-base.component';
import { PersistenceApiService } from '../../../../shared/services/persistence-api.service';
import { ApiService } from '../../../../shared/services/api.service';
import { FileRecordCreatePayload, FileRecordResponse } from '../../../../shared/services/persistence-api.service';
import { BaseFileResponse, ImageAnalysisResponse, StreamProgressEvent } from '../../../../shared/interfaces/fileUploadInterfaces';
import { Subject, throwError, from } from 'rxjs';
import { switchMap, take, takeUntil, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-project-documents',
  templateUrl: './project-documents.component.html',
  styleUrls: ['./project-documents.component.scss']
})
export class ProjectDocumentsComponent implements OnInit, OnDestroy {
  // --- INPUTS ---
  @Input() persistedFiles: PersistedFileInfo[] = [];
  @Input() isLoading = true;
  @Input() error: string | null = null;
  @Input() activeFilterFileId: string | null = null;
  @Input() currentUserEmail!: string;
  @Input() projectId!: string;
  @Input() hideIndexed = false;

  /** Files to display — optionally hides already-indexed files */
  get visibleFiles(): PersistedFileInfo[] {
    if (!this.hideIndexed) return this.persistedFiles;
    return this.persistedFiles.filter(f => !f.isEmbedded || f.status !== 'Success');
  }

  // --- OUTPUTS ---
  @Output() fileSelectedForView = new EventEmitter<PersistedFileInfo>();
  @Output() fileFilterChanged = new EventEmitter<PersistedFileInfo | null>();
  @Output() fileDeleted = new EventEmitter<void>();
  @Output() fileUploaded = new EventEmitter<FileRecordResponse>();
  @Output() indexAllRequested = new EventEmitter<void>();

  // --- INTERNAL STATE ---
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  allowedFileExtensions = ['.docx', '.txt', '.html', '.pdf', '.md', '.csv', '.json'];
  private destroy$ = new Subject<void>();

  // Drag State
  isDragOver = false;
  private dragCounter = 0;

  constructor(
    private apiService: ApiService,
    private persistenceApiService: PersistenceApiService
  ) { }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onIndexAllRequested(event: Event): void {
    event.stopPropagation();
    this.indexAllRequested.emit();
  }

  // --- EVENT HANDLERS ---
  onViewFile(file: PersistedFileInfo, event: Event): void {
    event.stopPropagation();
    this.fileSelectedForView.emit(file);
  }

  onFilterByRow(file: PersistedFileInfo, event: MouseEvent): void {
    if (event.target instanceof HTMLElement && event.target.closest('button')) { return; }
    if (file.status === 'Success' && file.id) {
      const newFilter = this.activeFilterFileId === file.id ? null : file;
      this.fileFilterChanged.emit(newFilter);
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  // -------------------------------------------------------------------------
  // DRAG AND DROP HANDLERS
  // -------------------------------------------------------------------------

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter++;
    this.isDragOver = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragCounter--;

    if (this.dragCounter <= 0) {
      this.isDragOver = false;
      this.dragCounter = 0;
    }
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    this.isDragOver = false;
    this.dragCounter = 0;

    const items = event.dataTransfer?.items;
    if (!items) return;

    const files = await this.getAllFileEntries(items);

    if (files.length > 0) {
      this.processFiles(files);
    }
  }

  private async getAllFileEntries(dataTransferItems: DataTransferItemList): Promise<File[]> {
    const fileEntries: File[] = [];
    const queue: any[] = [];

    for (let i = 0; i < dataTransferItems.length; i++) {
      const item = dataTransferItems[i].webkitGetAsEntry();
      if (item) queue.push(item);
    }

    while (queue.length > 0) {
      const entry = queue.shift();

      if (entry.isFile) {
        const file = await new Promise<File>((resolve) => entry.file(resolve));
        fileEntries.push(file);
      } else if (entry.isDirectory) {
        const dirReader = entry.createReader();
        const entries = await new Promise<any[]>((resolve) => {
          dirReader.readEntries((results: any[]) => resolve(results));
        });
        queue.push(...entries);
      }
    }

    return fileEntries;
  }


  // --- UPLOAD LOGIC ---

  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files && inputElement.files.length > 0) {
      const files = Array.from(inputElement.files);
      this.processFiles(files);
    }
    inputElement.value = ''; // Reset input
  }

  // Main processing loop with limit and confirmation
  private processFiles(files: File[]): void {
    if (!this.currentUserEmail || !this.projectId) {
      Swal.fire('Missing Context', 'Cannot upload file. User or Project info is missing.', 'error');
      return;
    }

    // 1. Filter valid files
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return this.allowedFileExtensions.includes(ext);
    });

    // Notify if skipped
    if (validFiles.length < files.length) {
      Swal.fire({
        toast: true, position: 'top-end', icon: 'warning',
        title: `Skipped ${files.length - validFiles.length} invalid files`,
        showConfirmButton: false, timer: 3000
      });
    }

    if (validFiles.length === 0) return;

    // 2. NEW: Check Limit (Max 10 files)
    const MAX_FILES = 10;
    if (validFiles.length > MAX_FILES) {
      Swal.fire({
        icon: 'warning',
        title: 'Too many files',
        text: `You have selected ${validFiles.length} files. The maximum allowed limit is ${MAX_FILES} files at a time.`,
        confirmButtonColor: '#435ebe'
      });
      return;
    }

    // Upload all files directly — PDFs always use vision
    validFiles.forEach(file => {
      const isPdf = file.name.toLowerCase().endsWith('.pdf');
      this.uploadSingleFile(file, isPdf);
    });
  }

  private uploadSingleFile(file: File, useVision: boolean = false): void {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    const contentTypeHint: PersistedFileInfo['contentType'] =
      fileExtension === '.txt' || fileExtension === '.md' ? 'text' :
        fileExtension === '.html' ? 'html' :
          fileExtension === '.docx' ? 'docx-text' :
            fileExtension === '.pdf' ? 'pdf' :
              undefined;

    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    const isVision = isPdf && useVision;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);

    // Set informative initial message based on file type
    let uploadMessage = 'Elaborazione file...';
    if (isVision) {
      uploadMessage = `Analisi visiva PDF in corso (${fileSizeMB} MB)...`;
    } else if (isPdf) {
      uploadMessage = `Estrazione testo dal PDF (${fileSizeMB} MB)...`;
    }

    const uploadEntry: PersistedFileInfo = {
      name: file.name,
      status: 'Uploading',
      timestamp: new Date(),
      message: uploadMessage,
      uploadedBy: this.currentUserEmail,
      isEmbedded: false
    };

    this.persistedFiles.unshift(uploadEntry);

    // Use streaming endpoint for vision PDFs (shows page progress)
    if (isVision) {
      this._uploadWithStreaming(file, uploadEntry, contentTypeHint);
      return;
    }

    // Non-vision: use standard upload
    this.apiService.uploadFile(file, false).pipe(
      switchMap((processResponse: BaseFileResponse | ImageAnalysisResponse) => {
        if (typeof processResponse.contents !== 'string') {
          return throwError(new Error('Processed content is not a string.'));
        }
        uploadEntry.status = 'Saving';
        uploadEntry.message = 'Salvataggio record...';

        const createPayload: FileRecordCreatePayload = {
          name: file.name,
          userName: this.currentUserEmail,
          projectName: this.projectId,
          contentType: contentTypeHint,
          content: processResponse.contents,
        };
        return this.persistenceApiService.createFileRecord(createPayload);
      }),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (savedRecord: FileRecordResponse) => {
        this.fileUploaded.emit(savedRecord);
        Swal.fire({
          toast: true, position: 'top-end', icon: 'success',
          title: 'Saved', text: savedRecord.name,
          showConfirmButton: false, timer: 2000
        });
      },
      error: (err) => {
        uploadEntry.status = 'Error';
        uploadEntry.message = 'Failed.';
        console.error(err);
      }
    });
  }

  /** Vision PDF upload with streaming progress (page x/y) */
  private _uploadWithStreaming(
    file: File,
    uploadEntry: PersistedFileInfo,
    contentTypeHint: PersistedFileInfo['contentType']
  ): void {
    let finalData: any = null;

    this.apiService.uploadFileStream(file, true).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (event: StreamProgressEvent) => {
        if (event.type === 'progress' && event.phase === 'vision') {
          uploadEntry.message = `Analisi visiva pagina ${event.current}/${event.total}...`;
        } else if (event.type === 'progress' && event.phase === 'saving') {
          uploadEntry.message = 'Salvataggio in GridFS...';
        } else if (event.type === 'complete') {
          finalData = event.data;
        } else if (event.type === 'error') {
          uploadEntry.status = 'Error';
          uploadEntry.message = event.detail || 'Errore durante upload.';
        }
      },
      error: (err) => {
        uploadEntry.status = 'Error';
        uploadEntry.message = 'Upload fallito.';
        console.error(err);
      },
      complete: () => {
        if (!finalData || uploadEntry.status === 'Error') return;

        uploadEntry.status = 'Saving';
        uploadEntry.message = 'Salvataggio record...';

        const createPayload: FileRecordCreatePayload = {
          name: file.name,
          userName: this.currentUserEmail,
          projectName: this.projectId,
          contentType: contentTypeHint,
          content: finalData.contents,
        };

        this.persistenceApiService.createFileRecord(createPayload)
          .pipe(take(1))
          .subscribe({
            next: (savedRecord: FileRecordResponse) => {
              this.fileUploaded.emit(savedRecord);
              Swal.fire({
                toast: true, position: 'top-end', icon: 'success',
                title: 'Saved', text: savedRecord.name,
                showConfirmButton: false, timer: 2000
              });
            },
            error: (err) => {
              uploadEntry.status = 'Error';
              uploadEntry.message = 'Salvataggio fallito.';
              console.error(err);
            }
          });
      }
    });
  }

  // --- DELETE LOGIC ---
  deletePersistedFile(file: PersistedFileInfo, event: Event): void {
    event.stopPropagation();
    if (!file.id) { return; }

    Swal.fire({
      title: `Delete ${file.name}?`,
      text: 'This will permanently remove the file. Cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        file.status = 'Deleting';
        this.persistenceApiService.deleteFileRecord(file.id!, this.currentUserEmail, this.projectId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.fileDeleted.emit();
              Swal.fire('Deleted!', `${file.name} deleted.`, 'success');
            },
            error: (err) => {
              file.status = 'Error';
              file.message = 'Delete failed.';
            }
          });
      }
    });
  }
}
